import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { claimAnonIdentity } from '@/lib/anonIdentity';
import type { User } from '@supabase/supabase-js';

const CLAIM_MARK_KEY = 'hpe_anon_claimed_v1';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      // Claim the anonymous identity once per browser after a SIGNED_IN event.
      // - Idempotent: sessionStorage mark prevents repeated calls across re-renders
      // - Non-destructive: claimAnonIdentity clears the local token only on
      //   HTTP success, so a network failure preserves the token for retry.
      if (event === 'SIGNED_IN' && session?.access_token) {
        try {
          if (sessionStorage.getItem(CLAIM_MARK_KEY)) return;
        } catch { /* ignore */ }
        claimAnonIdentity(session.access_token)
          .then((ok) => {
            if (ok) {
              try { sessionStorage.setItem(CLAIM_MARK_KEY, '1'); } catch { /* ignore */ }
            }
          })
          .catch(() => { /* preserve token for retry */ });
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, loading, isAuthenticated: !!user };
}
